import {
	Box,
	Divider,
	IconButton,
	Tooltip,
} from "@mui/material"
import {
	NavigationTabWrapper,
	NavigationTab,
	NavigationTabs,
	NavigationWrapper,
} from "../panels/Navigation"
import SearchNote from "./SearchNote"
import NoteTreeV2 from "./note_tree/NoteTreeV2"
import { useDispatch, useSelector } from "react-redux"
import { useSnackbar } from "notistack"
import { useContext, useEffect, useMemo, useState } from "react"
import { SocketContext } from "../../context/socket.context"
import { selectUserSub } from "../../reducers/userSlice"
import { useContextMenu } from "react-contexify"
import { clearNoteAction } from "../../reducers/noteActionSlice"
import PanelLoading from "../PanelLoading"
import NoteMenu from "./note_tree/NoteMenu"
import { UnfoldLess } from "@mui/icons-material"
import { reorderNotes } from "../../utils/noteUtilities"
import WorkspaceSearchBar from "./WorkSpaceSearchBar/WorkspaceSearchBar"
import {
	selectCurrentWorkspaceSearch,
	selectCurrentWorkspaceSearchIndex,
	selectCurrentWorkspaceSearchResults,
	selectIsSearching,
	selectSearches,
	selectIsWorkspaceSearchLoading
} from "../../reducers/workspaceSearchSlice"
import FormattedToolTip from "../../utils/FormattedToolTip"
import { UndoDeleteMenu } from "./undodelete/UndoDeleteMenu"
import ImportedArticleCounter from "./ImportedArticleCounter"
import {
	fetchTopLevelNotes,
	selectTopLevelNotes,
	selectLoading,
	clearAttachError,
	selectAttachError
} from "../../reducers/workspaceNoteSlice"
import {clearNotesOrder, makeSelectNotesOrder} from "../../reducers/noteOrderSlice"
import { selectShowNoteActionLoading } from "../../reducers/noteActionSlice"
import PanelLoadingWithBackdrop from "../PanelLoadingWithBackdrop"
import {selectIsLoading} from "../../reducers/undoDeleteNoteSlice"
import AddNote from "./note_tree/AddNote"
import {WorkspacePanelScrollControlPanel} from "./WorkspaceScrollControlPanel"
import {useWorkspaceContext} from "../../pages/Dashboard"
import {clearNoteAttachmentError, selectNoteAttachmentError} from "../../reducers/noteAttachmentsSlice"
import CopyCitations from "../dashboard/CopyCitations"
import {ManageWorkspaceModal} from "../manage_workspace_modal/ManageWorkspaceModal"
import LimitReachedModal from "../dashboard/LimitReachedModal"
import ShareArticleModal from "../dashboard/ShareArticleModal"

function WorkspacePanelNav({ workspace, currentSearches, currentSearchIndex }) {
	const dispatch = useDispatch()
	const { workspaceId } = useWorkspaceContext()
	return (
		<NavigationWrapper>
			<NavigationTabWrapper>
				<Tooltip title="Collapse Notes">
					<IconButton
						onClick={() => {
							dispatch(clearNoteAction(workspaceId))
							dispatch(clearNotesOrder(workspaceId))
							dispatch(fetchTopLevelNotes(workspaceId))
						}}
						sx={{ p: "10px", flexGrow: 0 }}
						aria-label="collapse"
					>
						<UnfoldLess color={"action"} />
					</IconButton>
				</Tooltip>
				<NavigationTabs value={0} aria-label="workspace-tabs">
					<Tooltip title={<FormattedToolTip
						title={workspace.title}
						description={workspace.description}
					/>}>
						<NavigationTab label={workspace.title} aria-label="workspace-tab" />
					</Tooltip>
				</NavigationTabs>
				<span style={{ flexGrow: 1 }} />
				<Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
				<UndoDeleteMenu />
			</NavigationTabWrapper>
			<WorkspaceSearchBar
				currentSearches={currentSearches}
				currentSearchIndex={currentSearchIndex}
			/>
		</NavigationWrapper>
	)
}

export default function WorkspacePanel({ workspace }) {
	// HOOKS
	const dispatch = useDispatch()
	const socket = useContext(SocketContext)
	const { enqueueSnackbar } = useSnackbar()
	const { workspaceId, compositeKey } = useWorkspaceContext()
	const { show, hideAll } = useContextMenu({ id: compositeKey })
	// REDUX SELECTORS
	const userSub = useSelector(selectUserSub)
	const isSearching = useSelector((state) => selectIsSearching(state, workspaceId))
	const currentWorkspaceSearches = useSelector((state) => selectSearches(state, workspaceId))
	const currentWorkspaceSearchIndex = useSelector((state) => selectCurrentWorkspaceSearchIndex(state, workspaceId))
	const areNotesLoading = useSelector((state) => selectLoading(state, workspaceId))
	const noteActionLoading = useSelector((state) => selectShowNoteActionLoading(state, workspaceId))
	const attachArticleError = useSelector((state) => selectAttachError(state, workspaceId))
	const isWorkspaceSearchLoading = useSelector((state) => selectIsWorkspaceSearchLoading(state, workspaceId))
	const undoDeleteIsLoading = useSelector((state) => selectIsLoading(state, workspaceId))
	const noteAttachmentError = useSelector((state) => selectNoteAttachmentError(state, workspaceId))
	// MEMOIZED COMPLEX SELECTORS
	const searchSelector = useMemo(() => selectCurrentWorkspaceSearch(workspaceId), [workspaceId])
	const search = useSelector(searchSelector)
	const searchResultsSelector = useMemo( () => selectCurrentWorkspaceSearchResults(workspaceId), [workspaceId] )
	const searchResults = useSelector(searchResultsSelector)
	const topLevelNotesSelector = useMemo(() => selectTopLevelNotes(workspaceId), [workspaceId])
	const topLevelNotes = useSelector(topLevelNotesSelector)
	const notesOrderSelector = useMemo(() => makeSelectNotesOrder(workspaceId), [workspaceId])
	const topLevelNoteOrder = useSelector((state) => notesOrderSelector(state, "root"))
	// LOCAL STATE
	const isPanelLoading = noteActionLoading || isWorkspaceSearchLoading || undoDeleteIsLoading
	const [showingContext, setShowingContext] = useState(false)

	useEffect(() => {
		let timer
		if (noteAttachmentError) {
			enqueueSnackbar(noteAttachmentError, { variant: "info" })
			timer = setTimeout(() => {
				dispatch(clearNoteAttachmentError(workspaceId))
			}, 3000)
		}

		return () => {
			if (timer) {
				clearTimeout(timer)
			}
		}
	}, [noteAttachmentError])

	useEffect(() => {
		let timer
		if (attachArticleError) {
			enqueueSnackbar(attachArticleError, { variant: "info" })
			timer = setTimeout(() => {
				dispatch(clearAttachError(workspaceId))
			}, 3000)
		}

		return () => {
			if (timer) {
				clearTimeout(timer)
			}
		}
	}, [attachArticleError])

	useEffect(() => {
		// client re-connect handler
		const onReconnect = () => {
			if(socket.recovered) {
				enqueueSnackbar("Reconnected to RefZoom Server", {variant: "success"})
				return // short-circuit to avoid redundant ENTER_WORKSPACE events
			}
			socket
				.timeout(5000)
				.emit("ENTER_WORKSPACE", workspaceId, (response) => {
					if (response?.error) {
						console.error("Reconnect ENTER_WORKSPACE failed:", response.error)
					}
				})
		}

		socket.io.on("reconnect", onReconnect)
		return () => socket.io.off("connect", onReconnect)
	}, [socket, workspaceId])

	useEffect(() => {
		// workspace connection handlers
		if (!userSub) return

		const unpackAndShowJoinOrLeaveToast = (payload) => {
			const {workspaceId: socketWorkspaceId, message} = payload
			if(socketWorkspaceId === workspaceId) {
				enqueueSnackbar(message, {variant: "info"})
			}
		}

		const onExclusions = ({msg}) => enqueueSnackbar(msg, {variant: "info"})


		socket.on("USER_JOINED_WORKSPACE", unpackAndShowJoinOrLeaveToast)
		socket.on("USER_LEFT_WORKSPACE", unpackAndShowJoinOrLeaveToast)
		socket.on("WORKSPACE_EXCLUSIONS_UPDATED", onExclusions)

		return () => {
			socket.off("USER_JOINED_WORKSPACE", unpackAndShowJoinOrLeaveToast)
			socket.off("USER_LEFT_WORKSPACE", unpackAndShowJoinOrLeaveToast)
			socket.off("WORKSPACE_EXCLUSIONS_UPDATED", onExclusions)
		}
	}, [socket, enqueueSnackbar, userSub, workspaceId])

	if (areNotesLoading) {
		return (
			<Box>
				<PanelLoading />
			</Box>
		)
	}

	return (
		<Box
			//id="panel-container"
			sx={{ backgroundColor: "primary.rOneBG", position: "relative", display: "flex", flexDirection: "column" }}
		>
			<WorkspacePanelNav
				workspace={workspace}
				currentSearches={currentWorkspaceSearches}
				currentSearchIndex={currentWorkspaceSearchIndex}
			/>
			<WorkspacePanelScrollControlPanel>
				{ !isSearching && topLevelNotes.length === 0 && // if a user deletes ALL notes from the workspace, show the AddNote input
					<AddNote note={{ workspaceId, id: null, path: "" }} />
				}

				{isSearching === true
					? searchResults.map((note) => (
						<SearchNote
							key={note.id}
							note={note}
							search={search}
						/>
					))
					: reorderNotes(topLevelNotes, topLevelNoteOrder).map((note, i) => (
						<NoteTreeV2
							index={i}
							key={note?.id}
							workspaceId={workspaceId}
							note={note}
							hideAll={hideAll}
							show={show}
							showingContext={showingContext}
						/>
					))}
			</WorkspacePanelScrollControlPanel>
			{workspace.ownerIsFreeTier && (
				<ImportedArticleCounter />
			)}
			<NoteMenu
				permissions={workspace.permissions}
				hideAll={hideAll}
				setShowingContext={setShowingContext}
				showingContext={showingContext}
				workspaceId={workspaceId}
			/>
			{isPanelLoading && (
				<PanelLoadingWithBackdrop isPanelLoading={isPanelLoading} />
			)}
			{/* WORKSPACE SPECIFIC MODALS (need to access useWorkspaceId provider */}
			<CopyCitations />
			<ManageWorkspaceModal />
			<LimitReachedModal />
			<ShareArticleModal />
		</Box>
	)
}